import React, { useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './WhyUs.css';
import certCz from './images/certCZ.webp';
import certEn from './images/certEN.webp';
import Lightbox from './Lightbox';
import { ArrowIcon, Reveal, SplitText } from './motion';

const ICONS = [
  // rychlost
  <path key="a" d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  // zkušenosti – ozubené kolo
  <g key="b">
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
  </g>,
  // tradice – budova
  <path key="c" d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" />,
  // kvalita – štít
  <path key="d" d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6zM8.5 12l2.5 2.5 4.5-5" />,
  // technologie – čip
  <g key="e">
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
  </g>,
];

// Karta certifikátu se jemně naklání podle kurzoru.
const TiltCard = ({ children, onClick, label }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 200, damping: 20 });

  return (
    <motion.button
      type="button"
      className="cert__card"
      aria-label={label}
      onClick={onClick}
      style={{ rotateX, rotateY }}
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onPointerLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
    >
      {children}
    </motion.button>
  );
};

const WhyUs = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const items = t('why.items', { returnObjects: true });
  const cert = { src: i18n.language === 'cs' ? certCz : certEn, alt: t('why.certAlt') };

  return (
    <section id="why" className="section section--light why">
      <div className="container">
        <div className="section-head">
          <SplitText text={t('why.title')} className="section-head__title" />
        </div>

        <div className="why__grid">
          <ul className="why__list">
            {items.map((label, i) => (
              <Reveal as="li" key={i} delay={i * 0.07} y={30} className="why__item">
                <svg viewBox="0 0 24 24" className="why__icon" aria-hidden="true">
                  {ICONS[i]}
                </svg>
                <span className="why__label">{label}</span>
              </Reveal>
            ))}
          </ul>

          <Reveal className="cert" delay={0.2}>
            <div className="cert__perspective">
              <TiltCard onClick={() => setOpen(true)} label={t('why.certShow')}>
                <img src={cert.src} alt={cert.alt} loading="lazy" />
                <span className="cert__badge">ISO 9001:2015</span>
              </TiltCard>
            </div>
            <div className="cert__text">
              <h3>{t('why.certTitle')}</h3>
              <p>{t('why.certText')}</p>
              <button type="button" className="btn btn--dark" onClick={() => setOpen(true)}>
                {t('why.certShow')}
                <ArrowIcon />
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {open && <Lightbox images={[cert]} index={0} onClose={() => setOpen(false)} onChange={() => {}} />}
      </AnimatePresence>
    </section>
  );
};

export default WhyUs;
